/**
 * Location Hierarchy Data Structure
 * Multi-level hierarchical location system for organizing boxes and items
 * Structure: Property → Category → Area → Specific Location
 */

// Properties (top level) - where the items are located
export const PROPERTIES = {
  "Home": { icon: "🏠", isDefault: true },
  "Summer House": { icon: "🏖️" },
  "Vacation Home": { icon: "🏡" },
  "Office": { icon: "🏢" },
  "Storage Unit": { icon: "📦" },
  "Parents' House": { icon: "👨‍👩‍👧" },
  "Rental Property": { icon: "🔑" },
};

// Default property for new locations
export const DEFAULT_PROPERTY = "Home";

// Categories (second level) - room types within a property
export const LOCATION_HIERARCHY = {
  "Bedroom": {
    icon: "🛏️",
    subcategories: {
      "Master": {
        subcategories: ["Closet", "Nightstand", "Under Bed", "Dresser", "Vanity", "Bathroom"]
      },
      "Guest": {
        subcategories: ["Closet", "Nightstand", "Under Bed", "Dresser"]
      },
      "Kid's": {
        subcategories: ["Closet", "Toy Box", "Under Bed", "Desk", "Bookshelf", "Dresser"]
      },
      "Spare": {
        subcategories: ["Closet", "Under Bed", "Storage", "Desk"]
      }
    }
  },
  "Kitchen": {
    icon: "🍳",
    subcategories: {
      "Cabinets": {
        subcategories: ["Upper", "Lower", "Pantry", "Island", "Corner", "Above Sink"]
      },
      "Counter": {
        subcategories: ["Main", "Island", "Bar", "Corner", "Window"]
      },
      "Appliances": {
        subcategories: ["Refrigerator", "Oven", "Microwave", "Dishwasher", "Freezer"]
      },
      "Dining Area": {
        subcategories: ["Table", "Buffet", "China Cabinet", "Corner"]
      }
    }
  },
  "Living Areas": {
    icon: "🏠",
    subcategories: {
      "Living Room": {
        subcategories: ["TV Stand", "Coffee Table", "Sofa", "Bookshelf", "Corner", "End Table"]
      },
      "Family Room": {
        subcategories: ["Entertainment Center", "Sectional", "Game Area", "Bookshelf"]
      },
      "Home Office": {
        subcategories: ["Desk", "Bookshelf", "Filing Cabinet", "Closet", "Corner"]
      },
      "Den": {
        subcategories: ["Desk", "Bookshelf", "Reading Chair", "Storage"]
      },
      "Entryway": {
        subcategories: ["Coat Closet", "Shoe Rack", "Console Table", "Bench"]
      }
    }
  },
  "Storage": {
    icon: "📦",
    subcategories: {
      "Garage": {
        subcategories: ["Shelves", "Workbench", "Floor", "Ceiling", "Cabinets", "Corner"]
      },
      "Basement": {
        subcategories: ["Shelves", "Floor", "Storage Room", "Workshop", "Under Stairs"]
      },
      "Attic": {
        subcategories: ["Boxes", "Shelves", "Floor", "Rafters", "Corner"]
      },
      "Storage Room": {
        subcategories: ["Shelves", "Floor", "Closet", "Corner"]
      },
      "Closet": {
        subcategories: ["Upper Shelf", "Lower Shelf", "Floor", "Hanging", "Door"]
      },
      "Shed": {
        subcategories: ["Shelves", "Floor", "Workbench", "Corner", "Ceiling"]
      }
    }
  },
  "Bathroom": {
    icon: "🚿",
    subcategories: {
      "Master": {
        subcategories: ["Cabinet", "Medicine Cabinet", "Shower", "Bathtub", "Vanity"]
      },
      "Guest": {
        subcategories: ["Cabinet", "Medicine Cabinet", "Shower", "Vanity"]
      },
      "Half Bath": {
        subcategories: ["Cabinet", "Medicine Cabinet", "Vanity"]
      },
      "Kids": {
        subcategories: ["Cabinet", "Medicine Cabinet", "Bathtub", "Vanity"]
      }
    }
  },
  "Outdoor": {
    icon: "🌳",
    subcategories: {
      "Patio": {
        subcategories: ["Furniture", "Storage Box", "Corner", "Umbrella Stand"]
      },
      "Deck": {
        subcategories: ["Furniture", "Storage Box", "Railing", "Corner"]
      },
      "Yard": {
        subcategories: ["Shed", "Garden", "Tool Area", "Play Area"]
      },
      "Mailbox": {
        subcategories: ["Inside", "Package Area"]
      },
      "Car": {
        subcategories: ["Trunk", "Glove Box", "Console", "Back Seat"]
      }
    }
  },
  "Utility": {
    icon: "🔧",
    subcategories: {
      "Laundry Room": {
        subcategories: ["Shelves", "Cabinets", "Floor", "Above Washer", "Corner"]
      },
      "Utility Room": {
        subcategories: ["Shelves", "Floor", "Workbench", "Corner"]
      },
      "Workshop": {
        subcategories: ["Workbench", "Tool Cabinet", "Shelves", "Floor", "Wall"]
      },
      "Mudroom": {
        subcategories: ["Lockers", "Bench", "Shelves", "Hooks", "Floor"]
      }
    }
  }
};

/**
 * Get all available properties (top level)
 */
export const getProperties = () => {
  return Object.keys(PROPERTIES);
};

/**
 * Get icon for a property
 */
export const getPropertyIcon = (property) => {
  const propertyData = PROPERTIES[property];
  return propertyData ? propertyData.icon : "📍";
};

/**
 * Get all available categories (second level)
 */
export const getCategories = () => {
  return Object.keys(LOCATION_HIERARCHY);
};

/**
 * Get subcategories for a given category
 */
export const getSubcategories = (category) => {
  const categoryData = LOCATION_HIERARCHY[category];
  if (!categoryData || !categoryData.subcategories) return [];
  return Object.keys(categoryData.subcategories);
};

/**
 * Get sub-subcategories for a given category and subcategory
 */
export const getSubSubcategories = (category, subcategory) => {
  const categoryData = LOCATION_HIERARCHY[category];
  if (!categoryData || !categoryData.subcategories) return [];
  
  const subcategoryData = categoryData.subcategories[subcategory];
  if (!subcategoryData || !subcategoryData.subcategories) return [];
  
  return subcategoryData.subcategories;
};

/**
 * Get icon for a category
 */
export const getCategoryIcon = (category) => {
  const categoryData = LOCATION_HIERARCHY[category];
  return categoryData ? categoryData.icon : "📍";
};

/**
 * Build full location path string
 */
export const buildLocationPath = (levels) => {
  return levels.filter(level => level && level.trim()).join(" - ");
};

/**
 * Parse location path string back to levels
 */
export const parseLocationPath = (locationPath) => {
  if (!locationPath) return [];
  return locationPath.split(" - ").map(level => level.trim()).filter(level => level);
};

/**
 * Validate if a location path exists in the hierarchy
 */
export const validateLocationPath = (levels) => {
  if (!levels || levels.length === 0) return false;
  
  const [category, subcategory, subSubcategory] = levels;
  
  // Check category exists
  if (!LOCATION_HIERARCHY[category]) return false;
  
  // If only category, it's valid
  if (levels.length === 1) return true;
  
  // Check subcategory exists
  const categoryData = LOCATION_HIERARCHY[category];
  if (!categoryData.subcategories || !categoryData.subcategories[subcategory]) return false;
  
  // If only category + subcategory, it's valid
  if (levels.length === 2) return true;
  
  // Check sub-subcategory exists
  const subcategoryData = categoryData.subcategories[subcategory];
  if (!subcategoryData.subcategories || !subcategoryData.subcategories.includes(subSubcategory)) return false;
  
  return true;
};

/**
 * Get recently used locations from localStorage
 */
export const getRecentlyUsedLocations = () => {
  try {
    const stored = localStorage.getItem('clutterlogix_recent_locations');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Return array of location path arrays, most recent first
      return parsed.slice(0, 5); // Limit to 5 most recent
    }
  } catch (error) {
    console.warn('Error reading recent locations from localStorage:', error);
  }
  return [];
};

/**
 * Add a location to recently used list
 */
export const addToRecentlyUsedLocations = (locationPath) => {
  try {
    if (!locationPath || locationPath.length === 0) return;
    
    // Get current recent locations
    let recentLocations = getRecentlyUsedLocations();
    
    // Remove if already exists (to move to top)
    recentLocations = recentLocations.filter(recent => 
      JSON.stringify(recent) !== JSON.stringify(locationPath)
    );
    
    // Add to beginning of array
    recentLocations.unshift(locationPath);
    
    // Keep only the 10 most recent
    recentLocations = recentLocations.slice(0, 10);
    
    // Save to localStorage
    localStorage.setItem('clutterlogix_recent_locations', JSON.stringify(recentLocations));
    
    console.log('Added to recent locations:', buildLocationPath(locationPath));
  } catch (error) {
    console.warn('Error saving recent location to localStorage:', error);
  }
};

/**
 * Clear recently used locations
 */
export const clearRecentlyUsedLocations = () => {
  try {
    localStorage.removeItem('clutterlogix_recent_locations');
  } catch (error) {
    console.warn('Error clearing recent locations:', error);
  }
};

/**
 * Get suggested locations based on item type or box name (placeholder for future implementation)
 */
export const getSuggestedLocations = (itemType, boxName) => {
  // This will be implemented with smart suggestions
  const suggestions = [];
  
  // Simple keyword-based suggestions for now
  const lowerBoxName = (boxName || '').toLowerCase();
  const lowerItemType = (itemType || '').toLowerCase();
  
  if (lowerBoxName.includes('cloth') || lowerItemType.includes('cloth')) {
    suggestions.push(["Bedroom", "Master", "Closet"]);
  }
  
  if (lowerBoxName.includes('kitchen') || lowerItemType.includes('kitchen')) {
    suggestions.push(["Kitchen", "Cabinets", "Upper"]);
  }
  
  if (lowerBoxName.includes('tool') || lowerItemType.includes('tool')) {
    suggestions.push(["Storage", "Garage", "Workbench"]);
  }
  
  return suggestions;
};
